import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { FiX, FiHeart } from "react-icons/fi";
import { apiRequest, errorMessage } from "../../api";
import { HELP_TYPES } from "./foundationUtils";

const inputClass = "w-full px-4 py-2.5 rounded-lg bg-white/5 border border-navy-border text-white focus:outline-none focus:border-accent-light";

// "Help this person" window: a generous visitor offers money, goods or time. The foundation team
// confirms it and coordinates - the offer is not shown publicly until an admin confirms it.
export default function HelpModal({ item, settings, onClose, onDone }) {
  const currency = settings?.currency_label || "PKR";
  const [form, setForm] = useState({ helper_name: "", email: "", phone: "", help_type: "money", amount: "", message: "", is_anonymous: false });
  const [status, setStatus] = useState("idle");   // idle | busy | done
  const [error, setError] = useState("");

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const set = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setStatus("busy");
    setError("");
    const { ok, data } = await apiRequest("/core/foundation/offers/", {
      method: "POST",
      body: { ...form, case: item.id, amount: form.help_type === "money" && form.amount !== "" ? form.amount : 0 },
    });
    if (!ok) {
      setStatus("idle");
      return setError(errorMessage(data));
    }
    setStatus("done");
    onDone?.();
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto"
      onClick={onClose}
    >
      <motion.div
        role="dialog"
        aria-modal="true"
        initial={{ opacity: 0, scale: 0.95, y: 12 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-lg my-auto bg-navy border border-navy-border rounded-2xl shadow-2xl p-7 text-white"
      >
        <button onClick={onClose} aria-label="Close" className="absolute top-4 right-4 text-gray-400 hover:text-white"><FiX size={20} /></button>

        {status === "done" ? (
          <div className="text-center py-6">
            <span className="w-14 h-14 mx-auto flex items-center justify-center rounded-full bg-emerald-500/15 text-emerald-400 mb-4"><FiHeart size={26} /></span>
            <h3 className="text-2xl font-bold">Thank you!</h3>
            <p className="text-gray-300 mt-2">Your offer to help <span className="text-accent-light">{item.display_name}</span> was received. Our team will contact you shortly and explain how to send your help safely.</p>
            <button onClick={onClose} className="mt-6 px-6 py-2.5 rounded-full bg-brand-gradient font-semibold">Close</button>
          </div>
        ) : (
          <>
            <h3 className="text-2xl font-bold flex items-center gap-2"><FiHeart className="text-accent-light" /> Help {item.display_name}</h3>
            <p className="text-sm text-gray-400 mt-1">{item.title}</p>

            {settings?.help_instructions && (
              <div className="mt-4 rounded-lg bg-accent/10 border border-accent-light/30 p-3 text-sm text-gray-200 whitespace-pre-line">
                {settings.help_instructions}
              </div>
            )}

            <form onSubmit={submit} className="space-y-3 mt-5">
              <input required placeholder="Your name" className={inputClass} value={form.helper_name} onChange={set("helper_name")} />
              <div className="grid grid-cols-2 gap-3">
                <input type="email" placeholder="Email" className={inputClass} value={form.email} onChange={set("email")} />
                <input placeholder="Phone" className={inputClass} value={form.phone} onChange={set("phone")} />
              </div>
              <p className="text-xs text-gray-500 -mt-1">Give at least one - only our team sees your contact details.</p>

              <div className="grid grid-cols-2 gap-3">
                <select className={`${inputClass} bg-navy-dark`} value={form.help_type} onChange={set("help_type")}>
                  {HELP_TYPES.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
                </select>
                {form.help_type === "money" ? (
                  <input required type="number" min="1" step="0.01" placeholder={`Amount (${currency})`} className={inputClass} value={form.amount} onChange={set("amount")} />
                ) : (
                  <div />
                )}
              </div>

              <textarea rows={3} placeholder="Message (optional) - what would you like to give, or a kind word" className={inputClass} value={form.message} onChange={set("message")} />

              <label className="flex items-center gap-2 text-sm text-gray-300">
                <input type="checkbox" checked={form.is_anonymous} onChange={(e) => setForm({ ...form, is_anonymous: e.target.checked })} />
                Keep my name private on the public page
              </label>

              {error && <p className="text-sm text-red-400">{error}</p>}
              <button disabled={status === "busy"} className="w-full py-2.5 rounded-lg bg-brand-gradient font-semibold shadow-md shadow-accent/20 disabled:opacity-60">
                {status === "busy" ? "Sending..." : "Offer to Help"}
              </button>
            </form>
          </>
        )}
      </motion.div>
    </motion.div>
  );
}
